/**
 * Slug rules mirror `backend/src/utils/marketplaceProfileUtils.ts` so client and server stay aligned.
 */
export function normalizeMarketplaceSlug(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const RESERVED = new Set([
  'api',
  'www',
  'admin',
  'settings',
  'help',
  'status',
  'health',
  'explore',
  'login',
  'sign-up',
  'influencer',
  'brands',
]);

const MIN_LEN = 1;
const MAX_LEN = 40;

export function isValidMarketplaceSlug(normalized: string): boolean {
  if (normalized.length < MIN_LEN || normalized.length > MAX_LEN) {
    return false;
  }
  if (RESERVED.has(normalized)) {
    return false;
  }
  return true;
}

export type MarketplaceSlugSaveValidation =
  | { valid: true; normalized: string }
  | { valid: false; title: string; description: string };

/**
 * Use when a non-empty slug is sent to the API (`includeSlug`).
 */
export function validateMarketplaceSlugForSave(raw: string): MarketplaceSlugSaveValidation {
  const n = normalizeMarketplaceSlug(raw);
  if (!n) {
    return {
      valid: false as const,
      title: 'Invalid page path',
      description: 'Use letters, numbers, and hyphens, or a simpler path without special characters.',
    };
  }
  if (!isValidMarketplaceSlug(n)) {
    return {
      valid: false as const,
      title: 'Page path not allowed',
      description: 'Use 1–40 characters, or a different path. Some words are reserved for the site.',
    };
  }
  return { valid: true as const, normalized: n };
}

export function getMarketplaceApiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
}
